import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import { ChartLineUp } from '@phosphor-icons/react'
import { NumberInputField } from './atoms/NumberInputField'
import { StatusOrb } from './atoms/StatusOrb'
import { MetricRow } from './molecules/MetricRow'
import { MetricSection } from './molecules/MetricSection'
import { BottleneckCard } from './molecules/BottleneckCard'
import { KeyMetricsGrid } from './molecules/KeyMetricsGrid'
import { OptimizationMap } from './molecules/OptimizationMap'
import { calcularSonarFunil, getSonarLabel, type FunnelMode, type SonarFunilInputs } from '../lib/sonarCalculations'
import { useRaioXState } from '../hooks/useRaioXState'
import { sonarFunilDefaultValues } from '../constants'
import type { MetricStatus } from '../types'

export function SonarPage() {
  const { values, updateValue } = useRaioXState({
    ...sonarFunilDefaultValues,
    mode: 'whatsapp' as FunnelMode,
  })

  const result = useMemo(() => {
    return calcularSonarFunil(values as SonarFunilInputs)
  }, [values])

  const keyMetrics = useMemo(() => {
    const keys = ['roas', 'consultaParaVenda', 'agendadaParaRealizada', 'qualificacao']
    return keys.map((k) => result.metricsByKey[k]).filter(Boolean)
  }, [result])

  const overallStatus = result.sonarStatus as MetricStatus | 'neutral'

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-[-0.022em] text-foreground">
            Sonar do Funil
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Diagnóstico completo das taxas de conversão
          </p>
        </div>
        <Tabs
          value={values.mode}
          onValueChange={(v) => updateValue('mode', v as FunnelMode)}
          className="w-auto"
        >
          <TabsList className="h-9 bg-muted p-0.5 gap-0.5">
            <TabsTrigger
              value="whatsapp"
              className="h-8 px-3 text-xs font-medium data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              WhatsApp Direto
            </TabsTrigger>
            <TabsTrigger
              value="landing-page"
              className="h-8 px-3 text-xs font-medium data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm"
            >
              Landing Page
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Dados e resultados */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] xl:grid-cols-[1fr_440px] gap-6 items-start">
        {/* LEFT: Input panel */}
        <Card className="bg-card border border-border rounded-2xl">
          <CardHeader className="pb-4 pt-5 px-5">
            <CardTitle className="text-sm font-semibold text-foreground tracking-[-0.022em]">
              Dados do Funil
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5 space-y-4">
            {/* Tráfego */}
            <GroupDivider label="Tráfego" />
            <NumberInputField
              id="investimento"
              label="Investimento"
              prefix="R$"
              stageNumber={1}
              value={values.investimento}
              onChange={(v) => updateValue('investimento', v)}
            />
            <NumberInputField
              id="impressoes"
              label="Impressões"
              stageNumber={2}
              value={values.impressoes}
              onChange={(v) => updateValue('impressoes', v)}
            />
            <NumberInputField
              id="cliques"
              label="Cliques"
              stageNumber={3}
              value={values.cliques}
              onChange={(v) => updateValue('cliques', v)}
            />

            {/* Engajamento */}
            <GroupDivider label="Engajamento" />
            {values.mode === 'landing-page' && (
              <NumberInputField
                id="pageViews"
                label="Page Views"
                stageNumber={4}
                value={values.pageViews}
                onChange={(v) => updateValue('pageViews', v)}
              />
            )}
            {values.mode === 'whatsapp' ? (
              <NumberInputField
                id="conversasWhatsapp-wa"
                label="Conversas WhatsApp"
                stageNumber={4}
                value={values.conversasWhatsapp}
                onChange={(v) => updateValue('conversasWhatsapp', v)}
              />
            ) : null}

            {/* Leads */}
            <GroupDivider label="Leads" />
            <NumberInputField
              id="leadsQualificados"
              label="Leads Qualificados"
              stageNumber={values.mode === 'landing-page' ? 5 : 5}
              value={values.leadsQualificados}
              onChange={(v) => updateValue('leadsQualificados', v)}
            />
            <NumberInputField
              id="leadsDesqualificados"
              label="Leads Desqualificados"
              value={values.leadsDesqualificados}
              onChange={(v) => updateValue('leadsDesqualificados', v)}
            />
            {values.mode === 'landing-page' && (
              <NumberInputField
                id="conversasWhatsapp-lp"
                label="Conversas WhatsApp"
                stageNumber={6}
                value={values.conversasWhatsapp}
                onChange={(v) => updateValue('conversasWhatsapp', v)}
              />
            )}

            {/* Consultas */}
            <GroupDivider label="Consultas" />
            <NumberInputField
              id="consultasAgendadas"
              label="Consultas Agendadas"
              stageNumber={values.mode === 'landing-page' ? 7 : 6}
              value={values.consultasAgendadas}
              onChange={(v) => updateValue('consultasAgendadas', v)}
            />
            <NumberInputField
              id="consultasRealizadas"
              label="Consultas Realizadas"
              stageNumber={values.mode === 'landing-page' ? 8 : 7}
              value={values.consultasRealizadas}
              onChange={(v) => updateValue('consultasRealizadas', v)}
            />

            {/* Vendas */}
            <GroupDivider label="Vendas" />
            <NumberInputField
              id="vendas"
              label="Vendas"
              stageNumber={values.mode === 'landing-page' ? 9 : 8}
              value={values.vendas}
              onChange={(v) => updateValue('vendas', v)}
            />
            <NumberInputField
              id="ticketMedio"
              label="Ticket Médio"
              prefix="R$"
              value={values.ticketMedio}
              onChange={(v) => updateValue('ticketMedio', v)}
            />
          </CardContent>
        </Card>

        {/* RIGHT: Results panel */}
        <div className="space-y-4 lg:sticky lg:top-6">
          {/* Overall status */}
          <Card className="bg-card border border-border rounded-2xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ChartLineUp weight="fill" className="w-4 h-4 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  Diagnóstico
                </span>
              </div>
              <Badge className="text-[10px] px-2 py-0.5 bg-primary/15 text-primary border-0 font-medium">
                {result.modeLabel}
              </Badge>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <StatusOrb status={overallStatus} className="w-2.5 h-2.5" />
              <div>
                <p className="text-base font-semibold text-foreground tracking-[-0.022em]">
                  {getSonarLabel(result.sonarStatus)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {result.primaryMetrics.filter((m) => m.status === 'success').length} de{' '}
                  {result.primaryMetrics.length} métricas primárias saudáveis
                </p>
              </div>
            </div>
          </Card>

          {/* Bottleneck */}
          <BottleneckCard
            bottleneck={result.bottleneck}
            bottleneckMetric={result.bottleneckMetric}
          />

          {/* Key metrics */}
          <KeyMetricsGrid metrics={keyMetrics} />

          {/* Primary metrics */}
          <MetricSection title="Métricas Primárias" count={result.primaryMetrics.length}>
            {result.primaryMetrics.map((m) => (
              <MetricRow key={m.key} metric={m} />
            ))}
          </MetricSection>

          {/* Secondary + interaction */}
          <MetricSection title="Métricas Secundárias">
            {result.secondaryMetrics.map((m) => (
              <MetricRow key={m.key} metric={m} />
            ))}
          </MetricSection>

          <MetricSection title="Métricas de Interação">
            {result.interactionMetrics.map((m) => (
              <MetricRow key={m.key} metric={m} />
            ))}
          </MetricSection>

        </div>
      </div>

      <OptimizationMap recommendations={result.recommendations} />
    </div>
  )
}

function GroupDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <Separator className="flex-1 bg-border/60" />
      <span className={cn('text-[10px] text-muted-foreground/60 uppercase tracking-[0.1em] px-1')}>
        {label}
      </span>
      <Separator className="flex-1 bg-border/60" />
    </div>
  )
}
